import { Outlet, useLocation, useParams } from 'react-router';
import {
    ApartmentOutlined,
    AssignmentOutlined,
    CalendarMonthOutlined,
    CorporateFareOutlined,
    PeopleOutlineOutlined,
} from '@mui/icons-material';
import { BaseLayout as UIBaseLayout } from '@dynamon-as/ui';
import type { BaseLayoutContentVariant, RenderLinkFn } from '@dynamon-as/ui';
import { NavigationGuardProvider } from '@contexts';
import { useDynolang } from '@hooks';
import {
    HeaderActions,
    HeaderBreadcrumbs,
    Logo,
    OrgLogo,
} from '@layouts/components';
import DepartmentNavLink from './components/DepartmentNavLink/DepartmentNavLink';
import type { DepartmentNavItem } from './components/DepartmentNavLink/DepartmentNavLink';
import type { FC } from 'react';

import { ORGANIZATION_PATHS } from '@pages/organization/config';
import {
    DEPARTMENT_BASE_PATH,
    DEPARTMENT_PATHS,
} from '@pages/departments/config';
import { EMPLOYEES_PATHS } from '@pages/employees/config';
import { PLANNING_PATHS } from '@pages/planning/config';

const getDepartmentNavItems = (
    departmentId: string | undefined,
    t: (path: string, fallback: string) => string,
): DepartmentNavItem[] => {
    // The sidebar only renders inside department routes, so the id is
    // always present; the fallback only satisfies the optional
    // `useParams` type.
    const id = departmentId ?? '';

    return [
        {
            id: 'min-organisasjon',
            label: t('my_org.title', 'Min organisasjon'),
            description: t(
                'my_org.description.label',
                'Oversikt over alle avdelinger',
            ),
            // Same icon as the organization home header (identity tile).
            icon: <CorporateFareOutlined />,
            href: '/',
        },
        {
            // Department home (dashboard) — labeled after the
            // organization home entry ("Min organisasjon").
            id: 'dashboard',
            label: 'Min avdeling',
            description: 'Her får du oversikten over avdelingen din',
            // Same icon as the department home header (identity tile).
            icon: <ApartmentOutlined />,
            href: DEPARTMENT_PATHS.root(id),
        },
        {
            id: 'employees',
            label: 'Ansatte',
            description:
                'Her finner du oversikt over alle ansatte i din avdeling og kan legge inn eller slette ansatte, samt spesifisere hvordan de skal arbeide',
            icon: <PeopleOutlineOutlined />,
            href: EMPLOYEES_PATHS.employees(id),
        },
        {
            id: 'planning',
            label: 'Planlegging',
            description:
                'Her planlegger du helg, høytider, ferie, avspasering og fravær for de ansatte i din avdeling',
            icon: <CalendarMonthOutlined />,
            href: PLANNING_PATHS.planning(id),
        },
        {
            id: 'tasks',
            label: 'Bemanning',
            description:
                'Her finner du de bemanningsplaner du har laget, eller oppretter ny bemanningsplan for din avdeling',
            icon: <AssignmentOutlined />,
            href: DEPARTMENT_PATHS.tasks(id),
        },
        // Department settings are not part of the sidebar navigation:
        // they are reached from the department dashboard's
        // "Innstillinger" action (`/departments/:id/settings/:tab`).
    ];
};

interface ResolvedDepartmentSection {
    /** First path segment below the department root. */
    section: string;
    /** Remaining sub-path segments below the section. */
    subSegments: string[];
}

/**
 * Resolves the department section of a pathname
 * (`/departments/:departmentId/...`).
 */
const resolveDepartmentSection = (
    pathname: string,
    departmentId: string | undefined,
): ResolvedDepartmentSection | undefined => {
    if (!departmentId) {
        return undefined;
    }

    const prefix = `${DEPARTMENT_BASE_PATH}/${departmentId}/`;
    if (pathname.startsWith(prefix)) {
        const [section = '', ...subSegments] = pathname
            .slice(prefix.length)
            .split('/');
        return { section, subSegments };
    }

    return undefined;
};

/**
 * Resolves the id of the navigation item matching the current route.
 * Department sections stay highlighted on their sub-pages, and
 * dual-mounted legacy sections map onto their canonical item.
 */
const getActiveNavId = (
    pathname: string,
    departmentId: string | undefined,
): string | undefined => {
    if (pathname === '/' || pathname === ORGANIZATION_PATHS.home) {
        return 'min-organisasjon';
    }

    // The department root is the department dashboard; sections are
    // highlighted from the first path segment below it.
    if (
        departmentId &&
        pathname === `${DEPARTMENT_BASE_PATH}/${departmentId}`
    ) {
        return 'dashboard';
    }

    const resolved = resolveDepartmentSection(pathname, departmentId);
    if (!resolved) {
        return undefined;
    }

    return resolved.section;
};

/**
 * Views that render their own edge-to-edge sub-navigation (e.g. Tabs
 * directly inside the content area) need the borderless content variant
 * so the sub-navigation bar is not wrapped in a bordered card.
 */
const PLAIN_CONTENT_SECTIONS = new Set(['employees', 'planning', 'settings']);

/* The staffing plans section: its detail/result pages are plain
 * but its list page is not. */
const TASKS_SECTIONS = new Set(['tasks']);

const usesPlainContentVariant = (
    pathname: string,
    departmentId: string | undefined,
): boolean => {
    const resolved = resolveDepartmentSection(pathname, departmentId);
    if (!resolved) {
        return false;
    }

    if (PLAIN_CONTENT_SECTIONS.has(resolved.section)) {
        return true;
    }

    /* The staffing plan detail page renders its own edge-to-edge Tabs
     * (TaskDetail → TaskForm), and so does its result page; the list
     * page (Tasks) has no tabs and uses the card variant. */
    return (
        TASKS_SECTIONS.has(resolved.section) && resolved.subSegments.length > 0
    );
};

// Adapts the DepartmentNavLink component to the library's renderLink
// contract (a plain function returning ReactNode).
const renderDepartmentNavLink: RenderLinkFn = (props) => (
    <DepartmentNavLink
        key={props.item.id}
        {...props}
    />
);

const SidebarLayoutContent: FC = () => {
    const { departmentId } = useParams<{ departmentId: string }>();
    const { pathname } = useLocation();
    const { t } = useDynolang('PAGE_ORG_AND_DEPARTMENTS');

    const contentVariant: BaseLayoutContentVariant = usesPlainContentVariant(
        pathname,
        departmentId,
    )
        ? 'plain'
        : 'card';

    return (
        <UIBaseLayout
            navItems={getDepartmentNavItems(departmentId, t)}
            activeId={getActiveNavId(pathname, departmentId)}
            contentVariant={contentVariant}
            renderLink={renderDepartmentNavLink}
            sidebarLogo={<Logo />}
            headerLogo={<OrgLogo />}
            headerActions={<HeaderActions />}
            headerBreadcrumb={<HeaderBreadcrumbs />}
            toggleLabel={t('navigation_section.toggle_sidebar.label', 'Skjul')}
            navAriaLabel={t(
                'navigation_section.nav_aria.label',
                'Hovednavigasjon',
            )}
            toggleAriaLabels={{
                expand: t(
                    'navigation_section.toggle_expand_aria.label',
                    'Vis sidefelt',
                ),
                collapse: t(
                    'navigation_section.toggle_collapse_aria.label',
                    'Skjul sidefelt',
                ),
            }}
        >
            <Outlet />
        </UIBaseLayout>
    );
};

const SidebarLayout: FC = () => (
    <NavigationGuardProvider>
        <SidebarLayoutContent />
    </NavigationGuardProvider>
);

export default SidebarLayout;
